import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Scale, Star, Home, DollarSign, FileText, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const LAWYER_IMAGE = 'https://customer-assets.emergentagent.com/job_ec80005d-fba4-4969-be74-853cc224eb78/artifacts/aelazn5y_Season_Spanski%20%283%29.jpeg';

// Florida counties for dropdown
const FLORIDA_COUNTIES = [
  "Alachua", "Baker", "Bay", "Bradford", "Brevard", "Broward", "Calhoun", "Charlotte", "Citrus", "Clay",
  "Collier", "Columbia", "DeSoto", "Dixie", "Duval", "Escambia", "Flagler", "Franklin", "Gadsden", "Gilchrist",
  "Glades", "Gulf", "Hamilton", "Hardee", "Hendry", "Hernando", "Highlands", "Hillsborough", "Holmes", "Indian River",
  "Jackson", "Jefferson", "Lafayette", "Lake", "Lee", "Leon", "Levy", "Liberty", "Madison", "Manatee",
  "Marion", "Martin", "Miami-Dade", "Monroe", "Nassau", "Okaloosa", "Okeechobee", "Orange", "Osceola", "Palm Beach",
  "Pasco", "Pinellas", "Polk", "Putnam", "Santa Rosa", "Sarasota", "Seminole", "St. Johns", "St. Lucie", "Sumter",
  "Suwannee", "Taylor", "Union", "Volusia", "Wakulla", "Walton", "Washington"
];

const LandingPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    phone: '',
    email: '',
    county: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/xvzzbdkp', {
        method: 'POST',
        body: JSON.stringify({
          firstName: formData.firstName,
          phone: formData.phone,
          email: formData.email,
          county: formData.county,
          source: 'Landing Page /lp'
        }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      q: "Do I need to come to your office?",
      a: "No. We serve all 67 Florida counties remotely. Everything is handled by phone, email, and electronic signature."
    },
    {
      q: "Can I change or cancel my Lady Bird Deed later?",
      a: "Yes. A Lady Bird Deed is fully revocable. You can change beneficiaries or cancel it entirely at any time during your lifetime."
    },
    {
      q: "Will this affect my homestead exemption or property taxes?",
      a: "In Florida, a properly drafted Lady Bird Deed does not trigger reassessment or affect your homestead exemption."
    },
    {
      q: "How is this different from just putting my child's name on the deed?",
      a: "Adding someone to your deed permanently gives them an ownership interest you cannot take back. A Lady Bird Deed keeps you in full control while achieving the same goal of passing the property without probate."
    }
  ];

  // Reusable form component
  const ConsultationForm = ({ darkBg = false }) => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isSubmitted ? (
        <div className={`p-6 rounded-lg text-center ${darkBg ? 'bg-[#c9a87c]/20' : 'bg-green-900/20 border border-green-500/30'}`}>
          <CheckCircle className="w-12 h-12 text-[#c9a87c] mx-auto mb-4" />
          <p className="font-serif text-xl text-[#faf9f7] mb-2">Thank you!</p>
          <p className="text-[#faf9f7]/70">Season's office will be in touch within 1 business day.</p>
        </div>
      ) : (
        <>
          <div>
            <input
              type="text"
              name="firstName"
              placeholder="First Name *"
              required
              value={formData.firstName}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#c9a87c]/30 rounded-sm text-[#faf9f7] placeholder:text-[#faf9f7]/40 focus:outline-none focus:border-[#c9a87c] transition-colors"
            />
          </div>
          <div>
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              required
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#c9a87c]/30 rounded-sm text-[#faf9f7] placeholder:text-[#faf9f7]/40 focus:outline-none focus:border-[#c9a87c] transition-colors"
            />
          </div>
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email *"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#c9a87c]/30 rounded-sm text-[#faf9f7] placeholder:text-[#faf9f7]/40 focus:outline-none focus:border-[#c9a87c] transition-colors"
            />
          </div>
          <div>
            <select
              name="county"
              required
              value={formData.county}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#c9a87c]/30 rounded-sm text-[#faf9f7] focus:outline-none focus:border-[#c9a87c] transition-colors appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23c9a87c'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
            >
              <option value="" className="bg-[#1a1a1a]">Select Your Florida County *</option>
              {FLORIDA_COUNTIES.map(county => (
                <option key={county} value={county} className="bg-[#1a1a1a]">{county} County</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-[#c9a87c] hover:bg-[#d4b896] text-[#0a0a0a] font-sans font-semibold text-lg rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Request My Free Lady Bird Deed Consultation →'}
          </button>
          <p className="text-center text-[#faf9f7]/50 text-sm">
            Flat fee $800-$1,000. No hidden costs. We serve all 67 Florida counties remotely.
          </p>
        </>
      )}
    </form>
  );

  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Florida — Free Consultation</title>
        <meta name="description" content="Florida Lady Bird Deed attorney serving all 67 counties. Protect your home from probate. Flat fee $800-$1,000. Free consultation — call 941-206-2223." />
        <link rel="canonical" href="https://thelawyerlady.com/lp" />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content="Lady Bird Deed Attorney Florida — Free Consultation" />
        <meta property="og:description" content="Florida Lady Bird Deed attorney serving all 67 counties. Protect your home from probate. Flat fee $800-$1,000." />
        <meta property="og:url" content="https://thelawyerlady.com/lp" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        
        {/* SECTION 1 — HERO */}
        <section className="relative py-8 md:py-16 lg:py-20 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#c9a87c]/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#c9a87c]/3 rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left - Content & Form */}
              <div className="order-2 lg:order-1">
                <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-[#faf9f7] leading-tight mb-6">
                  Protect Your Florida Home From Probate — <span className="text-[#c9a87c]">Free Consultation</span>
                </h1>
                <p className="font-sans text-lg text-[#faf9f7]/70 mb-8 leading-relaxed">
                  A Lady Bird Deed lets you pass your home directly to your heirs when you pass — without probate court, without losing control while you're alive. Florida attorney Season Spanski has helped homeowners across all 67 counties do exactly this.
                </p>
                
                {/* Form */}
                <div className="bg-[#141414] border border-[#c9a87c]/20 rounded-lg p-6 md:p-8">
                  <ConsultationForm />
                </div>
              </div>
              
              {/* Right - Photo */}
              <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute -inset-4 border border-[#c9a87c]/20 rounded-lg hidden lg:block" />
                  <img
                    src={LAWYER_IMAGE}
                    alt="Season A. Spanski, Florida Lady Bird Deed Attorney"
                    className="w-64 sm:w-72 lg:w-full lg:max-w-md rounded-lg shadow-2xl"
                  />
                  <div className="absolute bottom-4 left-4 right-4 bg-[#0a0a0a]/90 backdrop-blur-sm rounded-lg p-3 lg:p-4">
                    <p className="font-serif text-lg text-[#faf9f7]">Season A. Spanski</p>
                    <p className="font-sans text-sm text-[#c9a87c]">Florida Lady Bird Deed Attorney</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — TRUST BAR */}
        <section className="py-6 bg-[#141414] border-y border-[#c9a87c]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm md:text-base">
              <div className="flex items-center gap-2 text-[#faf9f7]/80">
                <Scale className="w-5 h-5 text-[#c9a87c]" />
                <span>Licensed Florida Attorney</span>
              </div>
              <span className="hidden md:inline text-[#c9a87c]/30">|</span>
              <div className="flex items-center gap-2 text-[#faf9f7]/80">
                <Star className="w-5 h-5 text-[#c9a87c]" />
                <span>5-Star Google Reviews</span>
              </div>
              <span className="hidden md:inline text-[#c9a87c]/30">|</span>
              <div className="flex items-center gap-2 text-[#faf9f7]/80">
                <Home className="w-5 h-5 text-[#c9a87c]" />
                <span>All 67 Florida Counties</span>
              </div>
              <span className="hidden md:inline text-[#c9a87c]/30">|</span>
              <div className="flex items-center gap-2 text-[#faf9f7]/80">
                <DollarSign className="w-5 h-5 text-[#c9a87c]" />
                <span>$800-$1,000 Flat Fee</span>
              </div>
              <span className="hidden md:inline text-[#c9a87c]/30">|</span>
              <div className="flex items-center gap-2 text-[#faf9f7]/80">
                <FileText className="w-5 h-5 text-[#c9a87c]" />
                <span>Free Consultation</span>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 — WHAT IS A LADY BIRD DEED */}
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] mb-8">
              You Keep Full Control. Your Heirs Skip Probate. <span className="text-[#c9a87c]">That's It.</span>
            </h2>
            <p className="font-sans text-lg text-[#faf9f7]/70 leading-relaxed mb-6">
              A Lady Bird Deed — also called an enhanced life estate deed — lets you name who gets your home when you pass, while keeping complete control during your lifetime. You can sell it, mortgage it, or change your mind anytime. When you pass, your heirs get the property automatically. No probate court. No delays. No attorney fees to transfer it.
            </p>
            <p className="font-sans text-lg text-[#c9a87c] italic">
              It is one of the most powerful estate planning tools available to Florida homeowners. And most people have never heard of it.
            </p>
          </div>
        </section>

        {/* SECTION 4 — TESTIMONIALS */}
        <section className="py-16 md:py-20 bg-[#0f0f0f]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] text-center mb-12">
              What Our Clients Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {/* Testimonial 1 */}
              <div className="bg-[#141414] border border-[#c9a87c]/10 rounded-lg p-6 md:p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#c9a87c] fill-[#c9a87c]" />
                  ))}
                </div>
                <p className="font-sans text-[#faf9f7]/80 leading-relaxed mb-6">
                  "Extremely professional and reasonably priced attorney. Helped me draw up a Florida Will, Power of Attorney, Healthcare Surrogate, and Lady Bird Deed. You will not be disappointed."
                </p>
                <div className="border-t border-[#c9a87c]/10 pt-4">
                  <p className="font-sans font-medium text-[#faf9f7]">— J.W.</p>
                  <p className="font-sans text-sm text-[#c9a87c]">Verified Google Review</p>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-[#141414] border border-[#c9a87c]/10 rounded-lg p-6 md:p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#c9a87c] fill-[#c9a87c]" />
                  ))}
                </div>
                <p className="font-sans text-[#faf9f7]/80 leading-relaxed mb-6">
                  "What could have felt overwhelming was made straightforward and stress-free. Every question was answered thoroughly. We now have peace of mind knowing our documents are properly structured. We highly recommend Spanski Law to anyone looking for trustworthy, compassionate legal guidance."
                </p>
                <div className="border-t border-[#c9a87c]/10 pt-4">
                  <p className="font-sans font-medium text-[#faf9f7]">— Lisa B.</p>
                  <p className="font-sans text-sm text-[#c9a87c]">Verified Google Review</p>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-[#141414] border border-[#c9a87c]/10 rounded-lg p-6 md:p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#c9a87c] fill-[#c9a87c]" />
                  ))}
                </div>
                <p className="font-sans text-[#faf9f7]/80 leading-relaxed mb-6">
                  "Attorney Spanski is knowledgeable and so helpful. She helped my parents make the best decisions and prevented a situation that could have had serious impact. I would not trust anyone else."
                </p>
                <div className="border-t border-[#c9a87c]/10 pt-4">
                  <p className="font-sans font-medium text-[#faf9f7]">— Dani</p>
                  <p className="font-sans text-sm text-[#c9a87c]">Verified Google Review</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 — HOW IT WORKS */}
        <section className="py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] text-center mb-12">
              How It Works — <span className="text-[#c9a87c]">Start to Finish</span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a87c]/10 flex items-center justify-center">
                  <span className="font-serif text-3xl text-[#c9a87c]">1</span>
                </div>
                <h3 className="font-serif text-xl text-[#faf9f7] mb-3">Free Consultation</h3>
                <p className="font-sans text-[#faf9f7]/70">
                  We review your property and goals to make sure a Lady Bird Deed is right for you.
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a87c]/10 flex items-center justify-center">
                  <span className="font-serif text-3xl text-[#c9a87c]">2</span>
                </div>
                <h3 className="font-serif text-xl text-[#faf9f7] mb-3">We Draft Your Deed</h3>
                <p className="font-sans text-[#faf9f7]/70">
                  We prepare your Lady Bird Deed with Florida-specific language that protects your rights.
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a87c]/10 flex items-center justify-center">
                  <span className="font-serif text-3xl text-[#c9a87c]">3</span>
                </div>
                <h3 className="font-serif text-xl text-[#faf9f7] mb-3">Sign & Record</h3>
                <p className="font-sans text-[#faf9f7]/70">
                  Sign, notarize, and record — your home is protected from probate.
                </p>
              </div>
            </div>

            <p className="text-center mt-12 font-sans text-[#c9a87c]">
              Most Lady Bird Deeds are completed within 2 weeks. Flat fee $800-$1,000. No surprises.
            </p>
          </div>
        </section>

        {/* SECTION 6 — WHO NEEDS THIS */}
        <section className="py-16 md:py-20 bg-[#0f0f0f]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] mb-8">
              If You Own a Home in Florida, <span className="text-[#c9a87c]">You Probably Need One.</span>
            </h2>
            <p className="font-sans text-lg text-[#faf9f7]/70 leading-relaxed">
              If you own a home in Florida and do not have a Lady Bird Deed or a trust, your home will likely go through probate when you pass. Probate is public, slow, and expensive. A Lady Bird Deed costs $800-$1,000 and eliminates that problem entirely.
            </p>
          </div>
        </section>

        {/* SECTION 7 — ABOUT SEASON */}
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] mb-8">
              Licensed Florida Attorney. <span className="text-[#c9a87c]">Statewide Remote Service.</span>
            </h2>
            <p className="font-sans text-lg text-[#faf9f7]/70 leading-relaxed">
              Season Spanski is a Florida-licensed estate planning attorney serving homeowners across all 67 Florida counties. She handles Lady Bird Deeds remotely — you do not need to travel to her Venice office. Everything can be completed from your home.
            </p>
          </div>
        </section>

        {/* SECTION 8 — FAQ */}
        <section className="py-16 md:py-20 bg-[#0f0f0f]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-[#141414] border border-[#c9a87c]/10 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="font-sans font-medium text-[#faf9f7] pr-4">{faq.q}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-[#c9a87c] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#c9a87c] flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="font-sans text-[#faf9f7]/70 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 9 — FINAL CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-serif text-3xl md:text-4xl text-[#faf9f7] mb-4">
                Ready to Protect Your Home?
              </h2>
              <p className="font-sans text-lg text-[#faf9f7]/70">
                Free consultation. Flat fee. Statewide service.
              </p>
            </div>
            
            <div className="bg-[#141414] border border-[#c9a87c]/20 rounded-lg p-6 md:p-8 mb-8">
              <ConsultationForm darkBg={true} />
            </div>
            
            <div className="text-center">
              <p className="font-sans text-[#faf9f7]/60 mb-2">Or call directly:</p>
              <a 
                href="tel:941-206-2223"
                className="inline-flex items-center gap-2 font-serif text-2xl text-[#c9a87c] hover:text-[#d4b896] transition-colors"
              >
                <Phone className="w-6 h-6" />
                (941) 206-2223
              </a>
            </div>
          </div>
        </section>

        {/* MINIMAL FOOTER */}
        <footer className="py-6 bg-[#050505] border-t border-[#c9a87c]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-[#faf9f7]/40">
              © 2026 Spanski Law PLLC | Florida Lady Bird Deed Attorney | <a href="tel:941-206-2223" className="text-[#c9a87c]/60 hover:text-[#c9a87c]">(941) 206-2223</a>
            </p>
          </div>
        </footer>

      </div>
    </>
  );
};

export default LandingPage;
